import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  accountType,
  activityLogType,
  errorTypes,
  firestoreCollections,
  formatActivityMessage,
  logUserActivity,
  merchAccountStatus,
  projectStatus,
} from "../utils";
import { merchantCategoriesGlobal } from "../utils/merchCategoriesGlobal";
import { db } from "../config/firebase";

export const baseQuery = createApi({
  reducerPath: "baseQuery",
  baseQuery: fakeBaseQuery(),

  tagTypes: [
    "unverified_merchants",
    "all_merchants",
    "merchant",
    "merchant_activity",
    "cust_merch_chat",
    "posted_projects",
    "active_projects",
    "completed_projects",
    "project_details",
    "all_customers",
  ],
  endpoints: (builder) => ({
    fetchUnverifiedMerchants: builder.query({
      // there is no need for realtime listener here, because with listener UI update will not take place and we'll then need to call dispatch to make UI updates, and without listener the data is cached for 60 seconds (default) and if we use the data within 60 seconds same data will be used, else new data will be fetched when compononet mounts, also when page is reloaded cached data is removed and new data is fetched. with realtime below codes need to performed
      /* 
      async queryFn(_arg, { dispatch }) {
        try {
          // Query for unverified merchants
          const q = query(
            collection(db, firestoreCollections.merchants),
            where("verified", "==", false),
            where("profileAdded", "==", true)
          );

          // Set up onSnapshot for real-time updates
          return new Promise(resolve => {
            const unsubscribe = onSnapshot(q, querySnapshot => {
              const unverifiedMerchants = querySnapshot.docs.map(doc => {
                const merchantData = doc.data();
                merchantData.id = doc.id;
                merchantData.category = merchantData.category.map(categ => {
                  const ENV =
                    process.env.REACT_APP_APP_CONFIG !== "production"
                      ? "DEV"
                      : "PROD";
                  const category = merchantCategoriesGlobal[ENV][categ];
                  return category;
                });
                return merchantData;
              });
              console.log(
                unverifiedMerchants,
                "<<< unverified merchants updated"
              );

              // Dispatch action to update the Redux state manually
              dispatch(
                baseQuery.util.updateQueryData(
                  "fetchUnverifiedMerchants",
                  undefined,
                  draft => {
                    return unverifiedMerchants;
                  }
                )
              );

              // Resolve the promise only once
              resolve({ data: unverifiedMerchants });
            });

            return () => unsubscribe();
          });
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
      */

      async queryFn() {
        try {
          const q = query(
            collection(db, firestoreCollections.merchants),
            where("verified", "==", false),
            where(
              "accountStatus.type",
              "==",
              merchAccountStatus.PROFILE_UNDER_REVIEW
            )
          );

          const querySnapshot = await getDocs(q);
          const unverifiedMerchants = querySnapshot.docs.map((doc) => {
            const merchantData = doc.data();
            merchantData.id = doc.id;
            merchantData.category = merchantData.category.map((categ) => {
              const ENV =
                process.env.REACT_APP_APP_CONFIG != "production"
                  ? "DEV"
                  : "PROD";
              const category = merchantCategoriesGlobal[ENV][categ];
              return category;
            });
            return merchantData;
          });
          console.log("unverified merch query run");
          return { data: unverifiedMerchants };
        } catch (err) {
          console.log(err, "<<< error unverified");
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
      providesTags: ["unverified_merchants"],
      //   refetchOnMountOrArgChange: true, // Refetch when the component mounts
      //   keepUnusedDataFor: 60 * 2, // Cache the data for 5 minutes
    }),
    validateMerchant: builder.mutation({
      invalidatesTags: ["unverified_merchants"],
      async queryFn({ merchantId }) {
        try {
          const docRef = doc(db, firestoreCollections.merchants, merchantId);
          const updatedData = {
            verified: true,
            accountStatus: { type: merchAccountStatus.ACTIVE },
          };
          await updateDoc(docRef, updatedData);
          return { data: null };
        } catch (err) {
          return {
            error: { status: errorTypes.UPDATE_ERROR, error: err.message },
          };
        }
      },
    }),
    denyMerchantRequest: builder.mutation({
      invalidatesTags: ["unverified_merchants"],
      async queryFn({ merchantId, reason }) {
        try {
          const docRef = doc(db, firestoreCollections.merchants, merchantId);
          const updatedData = {
            accountStatus: { type: merchAccountStatus.UNVERIFIED, reason },
            verified: false,
          };
          await updateDoc(docRef, updatedData);
          await logUserActivity(
            accountType.MERCHANT,
            merchantId,
            activityLogType.MERCH_PROFILE_DECLINED,
            { reason }
          );
          return { data: null };
        } catch (err) {
          return {
            error: { status: errorTypes.UPDATE_ERROR, error: err.message },
          };
        }
      },
    }),
    fetchAllMerchants: builder.query({
      providesTags: ["all_merchants"],
      async queryFn() {
        try {
          const q = query(
            collection(db, firestoreCollections.merchants),
            where("profileAdded", "==", true)
          );

          const querySnapshot = await getDocs(q);
          const verifiedMerchants = querySnapshot.docs.map((doc) => {
            const merchantData = doc.data();
            merchantData.id = doc.id;
            merchantData.category = merchantData.category.map((categ) => {
              const ENV =
                process.env.REACT_APP_APP_CONFIG != "production"
                  ? "DEV"
                  : "PROD";
              const category = merchantCategoriesGlobal[ENV][categ];
              return category;
            });
            return merchantData;
          });
          console.log("verified merch query run");
          return { data: verifiedMerchants };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
    }),
    fetchMerchantById: builder.query({
      providesTags: (result, error, { merchantId }) => [
        { type: "merchant", id: merchantId },
      ],
      async queryFn({ merchantId }) {
        try {
          const docRef = doc(db, firestoreCollections.merchants, merchantId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const merchantData = docSnap.data();
            merchantData.id = docSnap.id; // Include the document ID

            merchantData.category = merchantData.category.map((categ) => {
              const ENV =
                process.env.REACT_APP_APP_CONFIG != "production"
                  ? "DEV"
                  : "PROD";
              const category = merchantCategoriesGlobal[ENV][categ];
              return category;
            });

            return { data: merchantData };
          } else {
            // Handle case where the document does not exist
            return {
              error: {
                status: errorTypes.NOT_FOUND,
                error: "Merchant not found",
              },
            };
          }
        } catch (err) {
          return {
            error: { status: errorTypes.FETCH_ERROR, error: err.message },
          };
        }
      },
    }),
    updateMerchantById: builder.mutation({
      invalidatesTags: (result, error, { merchantId }) => [
        { type: "merchant", id: merchantId },
      ],
      async queryFn({ merchantId, updatedMerchData }) {
        try {
          const docRef = doc(db, firestoreCollections.merchants, merchantId);
          await updateDoc(docRef, updatedMerchData);

          //   this will update the cache data of fetchAllMerchants query, we are not using this because we are using the default cache time of fetchAllMerchants as 60seconds, so if any changes are made we are assuming it will take more than 60 seconds to make changes and update and hence when we move back to all merchants page, rtk will refetch the data
          //   dispatch(baseQuery.util.updateQueryData('fetchAllMerchants', undefined, draft => {
          //     const merchant = draft.find(m => m.id === merchantId);
          //     if (merchant) {
          //       merchant.phone = newPhoneNumber; // or whatever the field is
          //     }
          //   }));

          return { data: null };
        } catch (err) {
          return {
            error: { status: errorTypes.UPDATE_ERROR, error: err.message },
          };
        }
      },
    }),
    fetchMerchantActivityById: builder.query({
      // providesTags: (result, error, { merchantId }) => [
      //   { type: "merchant_activity", id: merchantId },
      // ],
      async queryFn({ merchantId }) {
        try {
          const q = query(
            collection(db, firestoreCollections.activityLogs),
            where("userId", "==", merchantId),
            orderBy("createdAt", "desc")
          );

          const querySnapshot = await getDocs(q);

          const activities = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          const formattedLogs = {};
          activities.forEach((activity) => {
            const date = activity.createdAt
              .toDate()
              .toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              });

            if (!formattedLogs[date]) {
              formattedLogs[date] = [];
            }
            formattedLogs[date].push({
              time: activity.createdAt.toDate().toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              }),
              createdAtTimestamp: activity.createdAt.toDate().getTime(), // Add timestamp for sorting
              message: formatActivityMessage(activity),
              details: activity?.details,
              activityLogType: activity?.activityLogType,
            });
          });

          // Sort logs within each date in descending order by time
          Object.keys(formattedLogs).forEach((date) => {
            formattedLogs[date].sort(
              (a, b) => b.createdAtTimestamp - a.createdAtTimestamp
            );
          });

          // Create a sorted result object with dates in descending order
          const sortedDates = Object.keys(formattedLogs).sort(
            (a, b) => new Date(b) - new Date(a)
          );
          const sortedFormattedLogs = {};

          console.log(sortedFormattedLogs, "<<< logs");

          sortedDates.forEach((date) => {
            sortedFormattedLogs[date] = formattedLogs[date];
          });

          return { data: sortedFormattedLogs };
        } catch (err) {
          console.log(err, "<<< error");
          return {
            error: { status: errorTypes.FETCH_ERROR, error: err.message },
          };
        }
      },
    }),
    fetchCustomerMerchantChat: builder.query({
      providesTags: (result, error, { merchantId, customerId }) => [
        { type: "" },
      ],
      async queryFn({ merchantId, customerId }) {
        try {
          const q = query(
            collection(db, firestoreCollections.chats),
            where("customerId", "==", customerId),
            where("merchantId", "==", merchantId)
          );

          const querySnapshot = await getDocs(q);
          const chats = querySnapshot.docs.map((doc) => {
            const chatData = doc.data();
            chatData.id = doc.id;
            return chatData;
          });
          console.log(chats, "chat query run");
          if (chats.length < 1)
            return {
              error: {
                status: errorTypes.FETCH_ERROR,
                error: "No record found.",
              },
            };
          else return { data: chats };
        } catch (err) {
          return {
            error: { status: errorTypes.FETCH_ERROR, error: err.message },
          };
        }
      },
    }),
    fetchPostedProjects: builder.query({
      providesTags: ["posted_projects"],
      async queryFn() {
        try {
          const q = query(
            collection(db, firestoreCollections.customerProjects),
            where("status", "in", [
              projectStatus.POSTED,
              projectStatus.ADMIN_DECLINED,
            ])
          );

          const querySnapshot = await getDocs(q);
          const postedProjects = await Promise.all(
            querySnapshot.docs.map(async (projectDoc) => {
              const projectData = projectDoc.data();
              projectData.id = projectDoc.id;
              const customerId = projectData.uploadedBy;
              if (customerId) {
                const customerDoc = await getDoc(
                  doc(db, firestoreCollections.customers, customerId)
                );
                if (customerDoc.exists()) {
                  const customerData = customerDoc.data();
                  projectData.uploadedByData = customerData; // Add customer data to project object
                }
              }

              projectData.category = projectData.category.map((categ) => {
                const ENV =
                  process.env.REACT_APP_APP_CONFIG != "production"
                    ? "DEV"
                    : "PROD";
                const category = merchantCategoriesGlobal[ENV][categ];
                return category;
              });
              return projectData;
            })
          );
          return { data: postedProjects };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
    }),
    fetchActiveProjects: builder.query({
      providesTags: ["active_projects"],
      async queryFn() {
        try {
          const q = query(
            collection(db, firestoreCollections.customerProjects),
            where("status", "==", projectStatus.ACTIVE),
            orderBy("createdAt", "desc")
          );

          const querySnapshot = await getDocs(q);
          const activeProjects = await Promise.all(
            querySnapshot.docs.map(async (projectDoc) => {
              const projectData = projectDoc.data();
              projectData.id = projectDoc.id;
              const customerId = projectData.uploadedBy;
              if (customerId) {
                const customerDoc = await getDoc(
                  doc(db, firestoreCollections.customers, customerId)
                );
                if (customerDoc.exists()) {
                  const customerData = customerDoc.data();
                  projectData.uploadedByData = customerData; // Add customer data to project object
                }
              }

              const merchantFinalizedId = projectData.merchantFinalized;
              const merchDoc = await getDoc(
                doc(db, firestoreCollections.merchants, merchantFinalizedId)
              );
              if (merchDoc.exists()) {
                const merchData = merchDoc.data();
                projectData.merchFinalizedData = merchData; // Add customer data to project object
              }

              projectData.category = projectData.category.map((categ) => {
                const ENV =
                  process.env.REACT_APP_APP_CONFIG != "production"
                    ? "DEV"
                    : "PROD";
                const category = merchantCategoriesGlobal[ENV][categ];
                return category;
              });
              return projectData;
            })
          );
          return { data: activeProjects };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
    }),
    fetchCompletedProjects: builder.query({
      providesTags: ["completed_projects"],
      async queryFn() {
        try {
          const q = query(
            collection(db, firestoreCollections.customerProjects),
            where("status", "==", projectStatus.COMPLETE),
            orderBy("completedOn", "desc")
          );

          const querySnapshot = await getDocs(q);
          const completedProjects = await Promise.all(
            querySnapshot.docs.map(async (projectDoc) => {
              const projectData = projectDoc.data();
              projectData.id = projectDoc.id;
              const customerId = projectData.uploadedBy;
              if (customerId) {
                const customerDoc = await getDoc(
                  doc(db, firestoreCollections.customers, customerId)
                );
                if (customerDoc.exists()) {
                  const customerData = customerDoc.data();
                  projectData.uploadedByData = customerData; // Add customer data to project object
                }
              }

              const merchantFinalizedId = projectData.merchantFinalized;
              const merchDoc = await getDoc(
                doc(db, firestoreCollections.merchants, merchantFinalizedId)
              );
              if (merchDoc.exists()) {
                const merchData = merchDoc.data();
                projectData.merchFinalizedData = merchData; // Add customer data to project object
              }

              projectData.category = projectData.category.map((categ) => {
                const ENV =
                  process.env.REACT_APP_APP_CONFIG != "production"
                    ? "DEV"
                    : "PROD";
                const category = merchantCategoriesGlobal[ENV][categ];
                return category;
              });
              return projectData;
            })
          );
          return { data: completedProjects };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
    }),
    fetchProjectBids: builder.query({
      providesTags: (result, error, { projectId }) => [
        { type: "project_bids", id: projectId },
      ],
      async queryFn({ projectId }) {
        try {
          const bidQuery = query(
            collection(db, firestoreCollections.projectsBids),
            where("projectId", "==", projectId),
            orderBy("createdAt", "desc")
          );

          const bidSnapshot = await getDocs(bidQuery);
          const bidsData = await Promise.all(
            bidSnapshot.docs.map(async (bidDoc) => {
              const bidData = bidDoc.data();
              bidData.id = bidDoc.id;

              const merchantRef = doc(
                db,
                firestoreCollections.merchants,
                bidData.merchantId
              );
              const merchantDoc = await getDoc(merchantRef);
              const merchantData = merchantDoc.data();

              return {
                ...bidData,
                merchantData: {
                  displayName: merchantData.displayName,
                  displayImage: merchantData.displayImage,
                },
              };
            })
          );

          return { data: bidsData };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
    }),
    deleteProjectBid: builder.mutation({
      invalidatesTags: (result, error, { projectId }) => [
        { type: "project_bids", id: projectId },
      ],
      async queryFn({ bidId }) {
        try {
          const bidRef = doc(db, firestoreCollections.projectsBids, bidId);
          await deleteDoc(bidRef);
          return { data: null };
        } catch (err) {
          return { error: { status: "DELETE_ERROR", error: err.message } };
        }
      },
    }),
    fetchProjectDetails: builder.query({
      providesTags: (result, error, { projectId }) => [
        { type: "project_details", id: projectId },
      ],
      async queryFn({ projectId }) {
        try {
          const projectRef = doc(
            db,
            firestoreCollections.customerProjects,
            projectId
          );
          const projectDoc = await getDoc(projectRef);

          if (!projectDoc.exists()) {
            return {
              error: {
                status: errorTypes.NOT_FOUND,
                error: "Project not found",
              },
            };
          }

          const projectData = projectDoc.data();
          projectData.id = projectId;

          const status = projectData.status;

          // Fetch customer data
          const customerId = projectData.uploadedBy;
          if (customerId) {
            const customerDoc = await getDoc(
              doc(db, firestoreCollections.customers, customerId)
            );
            if (customerDoc.exists()) {
              const customerData = customerDoc.data();
              projectData.uploadedByData = customerData;
            }
          }

          // For active and completed projects, fetch merchant data
          if (
            status === projectStatus.ACTIVE ||
            status === projectStatus.COMPLETE
          ) {
            const merchantFinalizedId = projectData.merchantFinalized;
            const merchDoc = await getDoc(
              doc(db, firestoreCollections.merchants, merchantFinalizedId)
            );
            if (merchDoc.exists()) {
              const merchData = merchDoc.data();
              projectData.merchFinalizedData = merchData;
            }

            // Fetch project checkpoints
            const checkpointsRef = collection(projectRef, "checkpoints");
            const checkpointsQuery = query(
              checkpointsRef,
              orderBy("no", "asc")
            );
            const checkpointsSnapshot = await getDocs(checkpointsQuery);

            const checkpoints = checkpointsSnapshot.docs.map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }));
            projectData.checkpoints = checkpoints;

            // Calculate project completion percentage
            const completedCheckpointsLength = checkpoints.filter(
              (item) => item.status === "COMPLETE"
            ).length;

            let projectCompletionPercentage = 0;
            if (checkpoints.length > 0) {
              projectCompletionPercentage = Math.floor(
                (completedCheckpointsLength / checkpoints.length) * 100
              );
            }
            projectData.currentProgress = projectCompletionPercentage;

            // Calculate total bill amount
            const total = projectData.bills.reduce(
              (acc, obj) => parseFloat(acc) + parseFloat(obj.amount),
              0
            );

            projectData.totalBillAmount = parseFloat(total);
          }

          // For posted projects, populate merchant data in conversations
          if (
            status === projectStatus.POSTED &&
            projectData.conversations?.length > 0
          ) {
            projectData.conversations = await Promise.all(
              projectData.conversations.map(async (conversation) => {
                const merchantRef = doc(
                  db,
                  firestoreCollections.merchants,
                  conversation.merchantId
                );
                const merchantDoc = await getDoc(merchantRef);
                const merchantData = merchantDoc.data();

                return {
                  ...conversation,
                  merchantData: {
                    displayName: merchantData.displayName,
                    displayImage: merchantData.displayImage,
                  },
                };
              })
            );
          }

          // Map categories
          projectData.category = projectData.category.map((categ) => {
            const ENV =
              process.env.REACT_APP_APP_CONFIG != "production" ? "DEV" : "PROD";
            const category = merchantCategoriesGlobal[ENV][categ];
            return category;
          });

          return { data: projectData };
        } catch (err) {
          return {
            error: { status: errorTypes.FETCH_ERROR, error: err.message },
          };
        }
      },
    }),
    addAdminResponse: builder.mutation({
      invalidatesTags: (result, error, { projectId }) => [
        { type: "project_details", id: projectId },
      ],
      async queryFn({ projectId, conversationId, adminResponse }) {
        try {
          const projectRef = doc(
            db,
            firestoreCollections.customerProjects,
            projectId
          );
          const projectDoc = await getDoc(projectRef);

          if (!projectDoc.exists()) {
            return {
              error: {
                status: errorTypes.NOT_FOUND,
                error: "Project not found",
              },
            };
          }

          const projectData = projectDoc.data();
          const updatedConversations = projectData.conversations.map((conv) => {
            if (conv.merchantId + conv.askedOn.seconds === conversationId) {
              return {
                ...conv,
                adminResponse: {
                  text: adminResponse,
                  answeredOn: new Date(),
                },
              };
            }
            return conv;
          });

          await updateDoc(projectRef, {
            conversations: updatedConversations,
          });

          return { data: null };
        } catch (err) {
          return {
            error: { status: errorTypes.UPDATE_ERROR, error: err.message },
          };
        }
      },
    }),
    deleteProjectQuestion: builder.mutation({
      invalidatesTags: (result, error, { projectId }) => [
        { type: "project_details", id: projectId },
      ],
      async queryFn({ projectId, questionId }) {
        try {
          const projectRef = doc(
            db,
            firestoreCollections.customerProjects,
            projectId
          );
          const projectDoc = await getDoc(projectRef);

          if (!projectDoc.exists()) {
            return {
              error: {
                status: errorTypes.NOT_FOUND,
                error: "Project not found",
              },
            };
          }

          const projectData = projectDoc.data();
          const updatedConversations = projectData.conversations.filter(
            (conv) => conv.merchantId + conv.askedOn.seconds !== questionId
          );

          await updateDoc(projectRef, {
            conversations: updatedConversations,
          });

          return { data: null };
        } catch (err) {
          return {
            error: { status: errorTypes.DELETE_ERROR, error: err.message },
          };
        }
      },
    }),
    fetchAllCustomers: builder.query({
      providesTags: ["all_customers"],
      async queryFn() {
        try {
          const q = query(collection(db, firestoreCollections.customers));

          const querySnapshot = await getDocs(q);
          const customers = querySnapshot.docs.map((doc) => {
            const customerData = doc.data();
            customerData.id = doc.id;
            return customerData;
          });
          console.log("customers query run");
          return { data: customers };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
    }),
    fetchCustomerById: builder.query({
      providesTags: (result, error, { customerId }) => [
        { type: "customer", id: customerId },
      ],
      async queryFn({ customerId }) {
        try {
          const docRef = doc(db, firestoreCollections.customers, customerId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const customerData = docSnap.data();
            customerData.id = docSnap.id; // Include the document ID
            return { data: customerData };
          } else {
            // Handle case where the document does not exist
            return {
              error: {
                status: errorTypes.NOT_FOUND,
                error: "Customer not found",
              },
            };
          }
        } catch (err) {
          return {
            error: { status: errorTypes.FETCH_ERROR, error: err.message },
          };
        }
      },
    }),
    fetchCustomerProjects: builder.query({
      providesTags: (result, error, { customerId }) => [
        { type: "customer_projects", id: customerId },
      ],
      async queryFn({ customerId }) {
        try {
          const q = query(
            collection(db, firestoreCollections.customerProjects),
            where("uploadedBy", "==", customerId)
          );

          const querySnapshot = await getDocs(q);
          const customerProjects = querySnapshot.docs.map((doc) => {
            const projectData = doc.data();
            projectData.id = doc.id;

            // Map categories
            projectData.category = projectData.category.map((categ) => {
              const ENV =
                process.env.REACT_APP_APP_CONFIG != "production"
                  ? "DEV"
                  : "PROD";
              const category = merchantCategoriesGlobal[ENV][categ];
              return category;
            });

            return projectData;
          });

          return { data: customerProjects };
        } catch (err) {
          return { error: { status: "FETCH_ERROR", error: err.message } };
        }
      },
    }),
  }),
});

export const {
  useFetchUnverifiedMerchantsQuery,
  useFetchAllMerchantsQuery,
  useValidateMerchantMutation,
  useFetchMerchantByIdQuery,
  useUpdateMerchantByIdMutation,
  useFetchCustomerMerchantChatQuery,
  useFetchMerchantActivityByIdQuery,
  useFetchCustomerByIdQuery,
  useDenyMerchantRequestMutation,
  useFetchPostedProjectsQuery,
  useFetchActiveProjectsQuery,
  useFetchCompletedProjectsQuery,
  useFetchProjectBidsQuery,
  useDeleteProjectBidMutation,
  useFetchProjectDetailsQuery,
  useAddAdminResponseMutation,
  useDeleteProjectQuestionMutation,
  useFetchAllCustomersQuery,
  useFetchCustomerProjectsQuery,
} = baseQuery;
